import { useMutation } from '@apollo/client';
import { Alert, AlertIcon, Box, CardProps, Flex, Link } from '@chakra-ui/react';
import { useAtom } from 'jotai';
import { useEffect, useState } from 'react';
import { IoMdInformationCircleOutline } from 'react-icons/io';
import { PiCreditCard } from 'react-icons/pi';

import { LeaseFriendsLogoWhite } from '../../../../assets/Images';
import PaymentInstrument from '../../../../components/referrals/PaymentInstrument';
import SendReferralLink from '../../../../components/referrals/SendReferralLink';
import { LDFlags } from '../../../../constants/flags';
import {
  ReferrerAggregateGraphQlType,
  useCreateReferrerLazyQuery,
  useGetOwedAsRefereeLazyQuery,
  useGetReferrerByCustomerIdLazyQuery,
} from '../../../../gql/generated/graphql';
import { createFinixIdentityGql, updateReferrerGql } from '../../../../gql/referrersGql';
import { useFlag } from '../../../../hooks';
import { logger } from '../../../../services/sentry';
import { customerReferrer } from '../../../../store/atom/referrer';
import { getISOWithOffset } from '../../../../utils/dates';
import { buildReferrerPayload, mapGraphReferrer } from '../../../../utils/referrals';
import { DealStateEnum } from '../../../../utils/types/deal';
import { FinixIdentity, Referrer } from '../../../../utils/types/referrer';
import { DealWithFarthestState, SHOW_REFERRAL_PANEL_STATES } from '../../utils';
import DashboardCard from '../DashboardCard';
import Metrics from './Metrics';
import ReferralLink from './ReferralLink';

type Props = {
  deal: DealWithFarthestState | undefined;
} & CardProps;

export default function ReferralProgram({ deal, ...props }: Props) {
  const customer_id: number | null = deal?.customer?.id ? Number(deal.customer.id) : null;
  const [fetchedCreated, setFetchedCreated] = useState<boolean>(false);
  const [needInstrument, setNeedInstrument] = useState<boolean>(false);
  const [owedAsRefereeCount, setOwedAsRefereeCount] = useState<number>(0);
  const [sendModalOpen, setSendModalOpen] = useState<boolean>(false);
  const [instrumentModalOpen, setInstrumentModalOpen] = useState<boolean>(false);
  const [aggregate, setAggregate] = useState<ReferrerAggregateGraphQlType | null>(null);
  const [referrer, setReferrer] = useAtom(customerReferrer);
  const [error, setError] = useState<string | null>(null);
  const comReferralProgram = useFlag(LDFlags.COM_REFERRAL_PROGRAM);

  const [getReferrer] = useGetReferrerByCustomerIdLazyQuery({
    context: { isErrorHandled: true },
  });
  const [createReferrer] = useCreateReferrerLazyQuery({
    context: { isErrorHandled: true },
  });
  const [getOwedAsReferee] = useGetOwedAsRefereeLazyQuery({
    context: { isErrorHandled: true },
  });
  const [createFinixIdentityMutation] = useMutation(createFinixIdentityGql);
  const [updateReferrerMutation] = useMutation(updateReferrerGql);

  const hideComponent =
    !customer_id ||
    !comReferralProgram ||
    !deal ||
    !SHOW_REFERRAL_PANEL_STATES.includes(deal.state as DealStateEnum);

  const fetchOrCreate = async () => {
    if (hideComponent || (referrer?.id ?? 0) > 0 || fetchedCreated) return;

    try {
      const existing = await getReferrer({ variables: { customer_id } });
      const existingData = existing.data?.getReferrerByCustomerId;

      if (existingData) {
        setReferrer(mapGraphReferrer(existingData));
        return;
      }

      const created = await createReferrer({ variables: { customer_id } });
      const createdData = created.data?.createReferrer;

      if (createdData) {
        setReferrer(mapGraphReferrer(createdData));
      }
    } catch (err) {
      logger.error(`ReferralProgram.tsx`, `fetchOrCreate`, [], err);
      setError(`There was a problem loading this portion of your dashboard`);
    } finally {
      setFetchedCreated(true);
    }
  };

  const createFinixIdentity = async (fields: FinixIdentity) => {
    try {
      const response = await createFinixIdentityMutation({
        variables: {
          ...fields,
        },
      });
      const { ensureFinixIdentity: id } = response.data || {};
      return id ?? null;
    } catch (err) {
      logger.error(`ReferralProgram.tsx`, `:110`, [], err);
      setError(`There was a problem loading this portion of your dashboard`);
    }
    return null;
  };

  const updateReferrer = async (fields: Partial<Referrer>, createIdentity?: FinixIdentity) => {
    if (!referrer) return null;
    try {
      const { id, ...rest } = { ...referrer, ...fields };
      const response = await updateReferrerMutation({
        variables: {
          id,
          data: {
            ...rest,
          },
        },
      });
      const updated = response.data?.updateReferrer;
      if (!updated) return null;
      let finix_id: string | null = null;
      if (createIdentity) {
        finix_id = await createFinixIdentity(createIdentity);
      }
      return { opted_in_at: updated.opted_in_at, finix_id };
    } catch (err) {
      logger.error(`ReferralProgram.tsx`, `:154`, [], err);
      setError(`There was a problem loading this portion of your dashboard`);
    }
    return null;
  };

  const handleOptIn = async () => {
    if (!referrer || !deal?.customer?.address) return;

    const localDateTime = getISOWithOffset(new Date());
    const { id: referrerId } = referrer;
    const { customer } = deal;

    const referrerPayload = buildReferrerPayload(referrerId, customer);

    const updatedReferrer = await updateReferrer(
      { opted_in_at: localDateTime, id: referrerId },
      referrerPayload,
    );

    if (updatedReferrer) {
      setReferrer({
        ...referrer,
        opted_in_at: updatedReferrer.opted_in_at,
        finix_identity: updatedReferrer.finix_id,
      });
    }
  };

  useEffect(() => {
    if (fetchedCreated && referrer && !referrer.opted_in_at) {
      handleOptIn();
    }
    if (referrer) {
      if (!referrer.finix_instrument) {
        const owedAsReferrer = aggregate && (aggregate.owed_referrals || 0) > 0;
        if (owedAsReferrer) {
          setNeedInstrument(true);
        } else if (customer_id) {
          getOwedAsReferee({ variables: { customer_id } })
            .then(({ data }) => {
              const count = data?.getOwedAsReferee ?? 0;
              setOwedAsRefereeCount(count);
              setNeedInstrument(count > 0);
            })
            .catch((err) => {
              logger.error(`ReferralProgram.tsx`, `getOwedAsReferee`, [], err);
            });
        }
      } else if (referrer.finix_instrument) {
        setInstrumentModalOpen(false);
        setNeedInstrument(false);
      }
    }
  }, [referrer, fetchedCreated, aggregate]);

  useEffect(() => {
    fetchOrCreate();
  }, [customer_id, comReferralProgram, deal?.state]);

  if (hideComponent) {
    return null;
  }

  return (
    <>
      <DashboardCard
        title="Referral Program"
        img={LeaseFriendsLogoWhite}
        headerColor="oceanBoatBlue"
        hasBorder
        mb={4}
        _textProps={{
          fontSize: 14,
          fontWeight: 700,
          textTransform: 'uppercase',
          color: 'white',
        }}
        {...props}
      >
        {error && (
          <Alert status="error" rounded="md">
            <AlertIcon />
            {error}
          </Alert>
        )}
        {referrer?.opted_in_at && (
          <>
            <Metrics
              referrer={referrer}
              needInstrument={needInstrument}
              aggregate={aggregate}
              setAggregate={setAggregate}
              owedAsRefereeCount={owedAsRefereeCount}
            />
            {needInstrument && (
              <Alert status="error" rounded="md" alignItems="flex-start">
                <AlertIcon as={IoMdInformationCircleOutline} />
                <Box>
                  <Link
                    onClick={() => setInstrumentModalOpen(true)}
                    fontWeight={700}
                    color="rgb(197,48,48)"
                  >
                    Enter payment information
                  </Link>{' '}
                  to receive your owed balance
                </Box>
              </Alert>
            )}
            {referrer.finix_instrument && referrer.finix_merchant && (
              <Flex alignItems="center">
                <Link
                  fontSize={14}
                  alignItems="center"
                  onClick={() => setInstrumentModalOpen(true)}
                >
                  <PiCreditCard /> Payment Details
                </Link>
              </Flex>
            )}
            <ReferralLink setSendModalOpen={setSendModalOpen} referrer={referrer} />
          </>
        )}
        {!referrer?.opted_in_at && (
          <Alert status="info" rounded="md" alignItems="flex-start">
            <AlertIcon as={IoMdInformationCircleOutline} />
            <Flex flexDir="column">
              <Box fontWeight={700}>Activating your program</Box>
              <Box>This will take a couple seconds.</Box>
            </Flex>
          </Alert>
        )}
      </DashboardCard>
      <SendReferralLink
        isOpen={sendModalOpen}
        setIsOpen={setSendModalOpen}
        code={referrer?.code || null}
        firstName={deal?.customer?.first_name || null}
        lastName={deal?.customer?.last_name || null}
      />
      <PaymentInstrument
        isOpen={instrumentModalOpen}
        setIsOpen={setInstrumentModalOpen}
        deal={deal}
      />
    </>
  );
}
